import { Link } from "wouter";
import { ArrowRight } from "lucide-react";
import { useBookCta } from "@/hooks/use-book-href";

interface BlogStayCtaProps {
  className?: string;
  variant?: "inline" | "card";
  /** Category slug of the post this renders on, used to pick contextual copy. */
  categorySlug?: string;
  /** Post slug, so the highest-traffic guides can carry bespoke copy. */
  postSlug?: string;
}

/**
 * Conversion CTA for blog and content pages.
 *
 * This block previously rendered byte-identical copy and anchor text on all 116
 * posts, which is the sitewide-link pattern playbook 05 warns against: search
 * engines discount templated links heavily, so the highest-impression articles
 * (the Lulumahu Falls permit guide alone draws ~1,348 impressions) passed almost
 * no targeted authority to the pages that earn revenue.
 *
 * Each category gets copy and an anchor phrase that relates to what the reader
 * is doing, so the link reads as editorial rather than boilerplate.
 *
 * The contextual anchor points at "/" because the homepage is the canonical page
 * for the category intent ("oahu beachfront vacation rentals"); /properties
 * owns the home-detail intent and carries the "See our homes" link.
 */
type CtaCopy = { eyebrow: string; heading: string; body: string; anchor: string };

// Every claim below is true of both homes: oceanfront in Ka'a'awa on the
// windward coast, near Kualoa Ranch, about 23 miles (30 minutes) from Honolulu airport,
// booked direct with no platform fees. One sleeps 8, the other 11. Nothing
// here says the two are next to each other.
const DEFAULT_COPY: CtaCopy = {
  eyebrow: "Ka'a'awa · Windward Oahu",
  heading: "Stay at one of our two oceanfront homes",
  body: "Two beach houses in Ka'a'awa, near Kualoa Ranch, sleeping 8 and 11. Book direct with no platform fees.",
  anchor: "Oahu beachfront vacation rentals",
};

const COPY_BY_CATEGORY: Record<string, CtaCopy> = {
  "hiking-outdoor-adventure": {
    eyebrow: "Ka'a'awa · Windward Oahu",
    heading: "Base yourselves on the windward coast",
    body: "Windward trailheads are far easier from this side of the island than from Waikiki. Our two homes sleep 8 and 11, so the whole group starts the day together.",
    anchor: "beachfront homes on Oahu's windward coast",
  },
  "beaches-coastal-guides": {
    eyebrow: "Ka'a'awa · Windward Oahu",
    heading: "Wake up on the water, not a shuttle schedule",
    body: "Both homes sit on the ocean in Ka'a'awa, with Mokoli'i in view. No parking hunt, no day-trip drive.",
    anchor: "Oahu oceanfront vacation rentals",
  },
  "snorkeling-water-activities": {
    eyebrow: "Ka'a'awa · Windward Oahu",
    heading: "Water access without the morning drive",
    body: "Staying oceanfront on the windward side puts you on the water early, when conditions are usually calmest and the crowds have not arrived.",
    anchor: "oceanfront beach houses on Oahu's windward side",
  },
  "food-local-culture": {
    eyebrow: "Ka'a'awa · Windward Oahu",
    heading: "Stay in the neighborhood, not the resort strip",
    body: "Ka'a'awa is a working windward community near Kualoa Ranch. Both homes have full kitchens for everything you bring home from the markets.",
    anchor: "Ka'a'awa beach house rentals near Kualoa Ranch",
  },
  "trip-planning-itineraries": {
    eyebrow: "Ka'a'awa · Windward Oahu",
    heading: "Somewhere to build the itinerary around",
    body: "Two oceanfront homes, sleeping 8 and 11, about 23 miles (30 minutes) from Honolulu airport. Booking direct means no platform fees and instant confirmation.",
    anchor: "Oahu beachfront vacation rentals for groups",
  },
  "transportation-practical-guides": {
    eyebrow: "Ka'a'awa · Windward Oahu",
    heading: "About 30 minutes from the airport",
    body: "One drive up the windward coast and you are done moving for the week. Parking is at the house, so the rental car sits still between trips.",
    anchor: "Oahu beach house rentals 30 minutes from Honolulu airport",
  },
};

// Per-article copy for the guides that carry the most search impressions,
// written by hand. No claim asserts a distance or drive time to the
// destination in question, only facts true of the homes themselves.
const COPY_BY_SLUG: Record<string, CtaCopy> = {
  "lulumahu-falls-permit-guide": {
    eyebrow: "Ka'a'awa · Windward Oahu",
    heading: "Sort the permit, then sort where you sleep",
    body: "Permits and early starts are easier when you are not driving out from the resort strip. Our two homes are on the windward coast, sleeping 8 and 11, and park everyone at the door.",
    anchor: "beachfront homes on Oahu's windward coast",
  },
  "lulumahu-falls-hidden-waterfall-guide": {
    eyebrow: "Ka'a'awa · Windward Oahu",
    heading: "A waterfall morning, then the ocean",
    body: "Trade the trail mud for the water the same afternoon. Both homes sit oceanfront in Ka'a'awa, near Kualoa Ranch.",
    anchor: "oceanfront beach houses in Ka'a'awa",
  },
  "traditional-luau-oahu-comparison": {
    eyebrow: "Ka'a'awa · Windward Oahu",
    heading: "Stay on the coast this culture still lives on",
    body: "Windward Oahu is a working community, not a resort district. Our two homes are in Ka'a'awa, near Kualoa Ranch, with Mokoli'i offshore.",
    anchor: "Ka'a'awa beachfront vacation rentals near Kualoa Ranch",
  },
  "oahu-weather-patterns-climate-guide": {
    eyebrow: "Ka'a'awa · Windward Oahu",
    heading: "Windward weather, from homes that face it",
    body: "The windward side keeps its own weather, which is worth understanding before you book. Ours are full-kitchen houses for the wet afternoons and the ocean for the rest.",
    anchor: "Oahu beachfront vacation rentals on the windward coast",
  },
  "ala-moana-beach-park-urban-guide": {
    eyebrow: "Ka'a'awa · Windward Oahu",
    heading: "The quiet alternative to a town beach",
    body: "Ala Moana is a good city beach. If you would rather skip the parking hunt altogether, both of our homes open straight onto the water in Ka'a'awa.",
    anchor: "Oahu oceanfront vacation rentals",
  },
  "aiea-loop-trail-rainforest-hike": {
    eyebrow: "Ka'a'awa · Windward Oahu",
    heading: "Trail in the morning, ocean by lunch",
    body: "Oahu is small enough to hike and swim the same day when you are not commuting from Waikiki. Our homes sleep 8 and 11 on the windward coast.",
    anchor: "beachfront homes on Oahu's windward coast",
  },
  "aiea-loop-trail-rainforest-guide": {
    eyebrow: "Ka'a'awa · Windward Oahu",
    heading: "Trail in the morning, ocean by lunch",
    body: "Oahu is small enough to hike and swim the same day when you are not commuting from Waikiki. Our homes sleep 8 and 11 on the windward coast.",
    anchor: "beachfront homes on Oahu's windward coast",
  },
};

function copyFor(categorySlug?: string, postSlug?: string): CtaCopy {
  return (
    (postSlug && COPY_BY_SLUG[postSlug]) ||
    (categorySlug && COPY_BY_CATEGORY[categorySlug]) ||
    DEFAULT_COPY
  );
}

export function BlogStayCta({ className = "", variant = "card", categorySlug, postSlug }: BlogStayCtaProps) {
  const copy = copyFor(categorySlug, postSlug);
  // Where "check availability" goes: Our Homes with two homes listed, the
  // booking page with one. The old literal /booking now answers 301, so
  // every guide was sending readers and crawlers through a redirect.
  const bookCta = useBookCta();

  if (variant === "inline") {
    return (
      <p className={`text-ink-soft leading-relaxed font-light ${className}`}>
        Planning your Oahu trip? Stay at one of our{" "}
        <Link href="/" className="text-ink underline decoration-sand-deep underline-offset-4 hover:text-ink-soft transition-colors">{copy.anchor}</Link>
        {" "}|{" "}
        <Link href={bookCta.href} className="text-ink underline decoration-sand-deep underline-offset-4 hover:text-ink-soft transition-colors font-normal">check availability and book direct</Link>.
      </p>
    );
  }

  return (
    <div
      className={`border border-sand-deep bg-sand px-8 py-8 ${className}`}
      data-testid="blog-stay-cta"
    >
      <div className="flex flex-col gap-8 sm:flex-row sm:items-center">
        <div className="flex-1">
          <p className="mb-3 font-sans text-[11px] uppercase tracking-[0.3em] text-driftwood">
            {copy.eyebrow}
          </p>
          <h2 className="mb-3 font-serif text-luxury-2xl font-normal leading-snug tracking-tight text-forest">
            {copy.heading}
          </h2>
          <p className="font-sans text-[15px] font-light leading-relaxed text-ink-soft">
            {copy.body}{" "}
            <Link
              href="/"
              className="border-b border-sand-deep text-ink transition-colors hover:border-forest hover:text-forest"
            >
              {copy.anchor}
            </Link>
            .
          </p>
        </div>

        <div className="flex shrink-0 flex-col items-start gap-4 sm:items-center">
          <Link
            href={bookCta.href}
            data-primary-book-cta
            className="inline-flex w-full items-center justify-center bg-sun px-8 py-4 font-sans tracking-wide text-shell transition-colors duration-300 hover:bg-sun-hover sm:w-auto"
          >
            Check availability
          </Link>
          <Link
            href="/properties"
            className="inline-flex min-h-[24px] items-center gap-2 border-b border-sand-deep pb-1 font-sans text-[11px] uppercase tracking-[0.24em] text-forest transition-colors hover:border-forest"
          >
            See our homes
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    </div>
  );
}
